import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import CodeBlock from './CodeBlock';

const ApiEndpoint = ({ endpoint, method, title, description, parameters, response, examples }) => {
  const [activeTab, setActiveTab] = useState('curl');
  const [testParams, setTestParams] = useState({});

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET':
        return 'text-green-400 bg-green-400/10';
      case 'POST':
        return 'text-blue-400 bg-blue-400/10';
      case 'PUT':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'DELETE':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-muted-foreground bg-muted/50';
    }
  };

  const handleParamChange = (paramName, value) => {
    setTestParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const generateTestRequest = () => {
    let testEndpoint = endpoint;
    const queryParams = [];
    
    parameters?.forEach(param => {
      const value = testParams?.[param?.name] || param?.example || '';
      if (param?.in === 'path') {
        testEndpoint = testEndpoint?.replace(`{${param?.name}}`, value);
      } else if (param?.in === 'query' && value) {
        queryParams?.push(`${param?.name}=${encodeURIComponent(value)}`);
      }
    });

    if (queryParams?.length > 0) {
      testEndpoint += '?' + queryParams?.join('&');
    }

    return testEndpoint;
  };

  const tabs = [
    { id: 'curl', label: 'cURL', icon: 'Terminal' },
    { id: 'javascript', label: 'JavaScript', icon: 'FileText' },
    { id: 'python', label: 'Python', icon: 'Code' },
    { id: 'php', label: 'PHP', icon: 'FileCode' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <span className={`px-2 py-1 text-xs font-medium rounded ${getMethodColor(method)}`}>
            {method?.toUpperCase()}
          </span>
          <code className="text-sm font-mono text-foreground bg-muted px-2 py-1 rounded">
            {endpoint}
          </code>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
          <p className="text-muted-foreground">{description}</p>
        </div>
      </div>
      {/* Parameters */}
      {parameters && parameters?.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Parameters</h4>
          <div className="space-y-3">
            {parameters?.map((param) => (
              <div key={param?.name} className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <code className="text-sm font-mono text-foreground">{param?.name}</code>
                    {param?.required && (
                      <span className="text-xs text-red-400">required</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{param?.type}</p>
                </div>
                <div className="lg:col-span-2 space-y-2">
                  <p className="text-sm text-muted-foreground">{param?.description}</p>
                  <Input
                    placeholder={param?.example || `Enter ${param?.name}`}
                    value={testParams?.[param?.name] || ''}
                    onChange={(e) => handleParamChange(param?.name, e?.target?.value)}
                    className="max-w-xs"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Try It Live */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">Try It Live</h4>
          <Button variant="outline" size="sm" iconName="Play">
            Send Request
          </Button>
        </div>
        <div className="p-4 bg-muted/30 rounded-lg">
          <p className="text-xs text-muted-foreground mb-2">Test Endpoint:</p>
          <code className="text-sm font-mono text-foreground bg-background px-3 py-2 rounded block">
            {generateTestRequest()}
          </code>
        </div>
      </div>
      {/* Code Examples */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground">Code Examples</h4>
        
        {/* Tabs */}
        <div className="flex space-x-1 bg-muted/30 p-1 rounded-lg">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Code Content */}
        <div className="space-y-4">
          {examples && examples?.[activeTab] && (
            <CodeBlock
              code={examples?.[activeTab]}
              language={activeTab}
              title={`${method?.toUpperCase()} ${endpoint}`}
              description={`Example ${activeTab} code for ${method?.toUpperCase()} request to ${endpoint}`}
            />
          )}
        </div>
      </div>
      {/* Response */}
      {response && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Response</h4>
          <CodeBlock
            code={JSON.stringify(response, null, 2)}
            language="json"
            title="Example Response"
            description="Example JSON response structure for this endpoint"
          />
        </div>
      )}
    </div>
  );
};

export default ApiEndpoint;